import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  onSnapshot,
  query,
  orderBy,
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { Inversion } from '../../../shared/types';

export class InversionRepository {
  private getCollectionPath(userId: string): string {
    return `users/${userId}/inversiones`;
  }

  async create(userId: string, inversion: Omit<Inversion, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.getCollectionPath(userId)), {
        ...inversion,
        fecha: Timestamp.fromDate(inversion.fecha),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating inversion:', error);
      throw new Error('Error al crear la inversión');
    }
  }

  async update(userId: string, id: string, inversion: Partial<Omit<Inversion, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    try {
      const docRef = doc(db, this.getCollectionPath(userId), id);
      await updateDoc(docRef, {
        ...inversion,
        fecha: inversion.fecha ? Timestamp.fromDate(inversion.fecha) : undefined,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating inversion:', error);
      throw new Error('Error al actualizar la inversión');
    }
  }

  async delete(userId: string, id: string): Promise<void> {
    try {
      const docRef = doc(db, this.getCollectionPath(userId), id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting inversion:', error);
      throw new Error('Error al eliminar la inversión');
    }
  }

  async getAll(userId: string): Promise<Inversion[]> {
    try {
      const q = query(
        collection(db, this.getCollectionPath(userId)),
        orderBy('fecha', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          concepto: data.concepto,
          monto: data.monto,
          fecha: data.fecha.toDate(),
          tipo: data.tipo,
          descripcion: data.descripcion,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        };
      });
    } catch (error) {
      console.error('Error getting inversiones:', error);
      throw new Error('Error al obtener las inversiones');
    }
  }

  subscribeToInversiones(userId: string, callback: (inversiones: Inversion[]) => void): () => void {
    const q = query(
      collection(db, this.getCollectionPath(userId)),
      orderBy('fecha', 'desc')
    );

    return onSnapshot(q, (querySnapshot) => {
      const inversiones = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          concepto: data.concepto,
          monto: data.monto,
          fecha: data.fecha.toDate(),
          tipo: data.tipo,
          descripcion: data.descripcion,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        };
      });
      callback(inversiones);
    }, (error) => {
      console.error('Error in inversiones subscription:', error);
    });
  }

  async getByType(userId: string, tipo: string): Promise<Inversion[]> {
    try {
      const q = query(
        collection(db, this.getCollectionPath(userId)),
        where('tipo', '==', tipo),
        orderBy('fecha', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          concepto: data.concepto,
          monto: data.monto,
          fecha: data.fecha.toDate(),
          tipo: data.tipo,
          descripcion: data.descripcion,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        };
      });
    } catch (error) {
      console.error('Error getting inversiones by type:', error);
      throw new Error('Error al obtener las inversiones por tipo');
    }
  }

  async getByDateRange(userId: string, startDate: Date, endDate: Date): Promise<Inversion[]> {
    try {
      const q = query(
        collection(db, this.getCollectionPath(userId)),
        where('fecha', '>=', Timestamp.fromDate(startDate)),
        where('fecha', '<=', Timestamp.fromDate(endDate)),
        orderBy('fecha', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          concepto: data.concepto,
          monto: data.monto,
          fecha: data.fecha.toDate(),
          tipo: data.tipo,
          descripcion: data.descripcion,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
        };
      });
    } catch (error) {
      console.error('Error getting inversiones by date range:', error);
      throw new Error('Error al obtener las inversiones por rango de fechas');
    }
  }
}
